import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Gavel, Play, Eye, Loader2, RefreshCw, ShieldCheck, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C', violet: '#6C4BB5' };

const OUTCOME_COLOR = {
  issue: C.green, bind: C.green, settle: C.green, applied: C.green, apply: C.green,
  counter_signed: C.green, cancelled_insured_request: C.blue, cancelled_non_payment: C.amber,
  cancelled_non_disclosure: C.red, cancelled_on_notice: C.red, notice_served: C.amber,
  notice_withdrawn: C.green, lapsed_at_expiry: C.slate, decline: C.red, declined: C.red,
  lapsed: C.amber, breach_accepted: C.red, info_requested: C.blue, closed_no_response: C.slate,
  legacy_reported: C.slate, closed_legacy: C.slate, no_quantum: C.amber, waiting: C.slate,
  assessor_appointed: C.blue, test_artifact: C.slate, backlog: C.slate,
  held_for_human: C.violet, failed: C.red,
};

const EDITABLE = [
  { key: 'refer_issue_ceiling', label: 'Refer ceiling', hint: 'a composite above this declines' },
  { key: 'claims_ceiling', label: 'Settlement ceiling', hint: 'above this an assessor is appointed, not a payment' },
  { key: 'rate_change_cap_pct', label: 'Rate cap %', hint: 'the loop may move rates this far alone' },
  { key: 'rate_drift_cap_ratio', label: 'Drift cap ×', hint: 'how far from the calibrated card a class may sit before a human decides' },
  { key: 'info_wait_days', label: 'Answer window', hint: 'days before an unanswered enquiry closes' },
  { key: 'assessor_wait_days', label: 'Assessor window', hint: 'days before an unreported claim settles' },
  { key: 'batch_limit', label: 'Items per pass', hint: 'how much one pass disposes of' },
];

const money = n => (n === null || n === undefined ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

function Chip({ children, color, bg, testid }) {
  return (
    <span data-testid={testid} className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
      style={{ color, background: bg }}>{children}</span>
  );
}

function Tile({ label, value, color, sub, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-xl font-bold leading-none" style={{ color }}>{value}</p>
      <p className="text-[10px] uppercase mt-1 leading-tight" style={{ color: C.slate }}>{label}</p>
      {sub && <p className="text-[10px]" style={{ color: C.slate }}>{sub}</p>}
    </div>
  );
}

export const AutonomyCard = () => {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState(null);
  const [edits, setEdits] = useState({});

  const load = useCallback(() => {
    api.get('/autonomy', { params: { limit: 40 } })
      .then(({ data: d }) => { setData(d); setEdits({}); })
      .catch(() => toast.error('Could not load the adjudicator'));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const run = async (dry) => {
    setBusy(dry ? 'dry' : 'live');
    try {
      const { data: r } = await api.post('/autonomy/run', { dry_run: dry }, { timeout: 600000 });
      if (dry) { setPreview(r); toast.success(`Preview: ${r.decided} item(s) would be disposed of`); }
      else { setPreview(null); toast.success(`${r.decided} item(s) disposed of`); load(); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'The pass could not complete'); }
    setBusy('');
  };

  const saveConfig = async (patch) => {
    setBusy('cfg');
    try {
      await api.put('/autonomy/config', { patch });
      toast.success('Parameter saved — the next pass uses it');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save that'); }
    setBusy('');
  };

  if (!data) return null;
  const cfg = data.config || {};
  const last = (data.cycles || [])[0] || {};
  const R = last.resolvers || {};
  const shown = preview?.resolvers || R;
  const carve = data.carve_outs || { items: [] };

  return (
    <Card className="rounded-sm border-l-4 bg-white p-5" data-testid="autonomy-card" style={{ borderLeftColor: C.violet }}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="max-w-2xl">
          <h3 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2" style={{ color: C.navy }}>
            <Gavel className="w-4 h-4" style={{ color: C.violet }} />
            The adjudicator — nothing waits for a person
            <Chip color="#5B3E9E" bg="rgba(108,75,181,0.12)" testid="autonomy-armed">running</Chip>
          </h3>
          <p className="text-xs mt-1.5 leading-relaxed" style={{ color: C.slate }}>
            Referrals, claims, cancellations, renewal bind blocks, the counter-signature and the improvement loop all
            dispose of themselves. Gates are applied, never bypassed: where the ABR gate offers an override the system
            takes it and signs its own name to it; where it does not, the risk is declined. On a claim the onus of
            proving an exclusion is ours, so unresolved doubt is paid rather than parked.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={load} className="p-2 rounded hover:bg-slate-50" style={{ color: C.slate }} data-testid="autonomy-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          {isAdmin && (
            <>
              <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run(true)}
                className="rounded-sm text-xs" data-testid="autonomy-preview-btn">
                {busy === 'dry' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Eye className="w-3.5 h-3.5 mr-1" /> Preview</>}
              </Button>
              <Button size="sm" disabled={!!busy} onClick={() => run(false)}
                className="rounded-sm text-xs" style={{ background: C.violet }} data-testid="autonomy-run-btn">
                {busy === 'live' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Play className="w-3.5 h-3.5 mr-1" /> Run a pass</>}
              </Button>
            </>
          )}
        </div>
      </div>

      {preview && (
        <p className="text-[11px] mt-3 px-2 py-1.5 rounded-sm" style={{ background: 'rgba(26,76,124,0.07)', color: C.blue }} data-testid="autonomy-preview-banner">
          Showing a preview — nothing below has been written. {preview.decided} item(s) would be disposed of.
        </p>
      )}

      <div className="flex gap-5 flex-wrap mt-4 pt-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
        <Tile label="disposed of, last pass" value={(preview || last).decided ?? 0} color={C.violet} testid="autonomy-decided" />
        <Tile label="risks written" value={(shown.referrals?.issued ?? 0) + (shown.renewal_blocks?.bound ?? 0)} color={C.green} testid="autonomy-issued"
          sub={`${shown.referrals?.declined ?? 0} declined`} />
        <Tile label="claims settled" value={shown.claims?.settled ?? 0} color={C.green} testid="autonomy-settled"
          sub={shown.claims?.paid_total ? `${money(shown.claims.paid_total)} paid` : null} />
        <Tile label="claims declined" value={shown.claims?.declined ?? 0} color={C.red} testid="autonomy-claims-declined"
          sub={shown.claims?.assessors_appointed ? `${shown.claims.assessors_appointed} to an assessor` : null} />
        <Tile label="policies cancelled" value={['insured_request', 'expiry_lapse', 'notices_executed'].reduce((a, k) => a + (shown.cancellations?.[k] ?? 0), 0)}
          color={C.amber} testid="autonomy-cancelled" sub={shown.cancellations?.refunded ? `${money(shown.cancellations.refunded)} returned` : null} />
        <Tile label="notices served" value={shown.cancellations?.notices_served ?? 0} color={C.amber} testid="autonomy-notices"
          sub={`${shown.cancellations?.notices_running ?? 0} clock(s) running · ${shown.cancellations?.notices_withdrawn ?? 0} cured`} />
        <Tile label="binds attested" value={shown.signatures?.signed ?? 0} color={C.blue} testid="autonomy-attested" />
        <Tile label="parameters changed" value={shown.improvements?.applied ?? 0} color={C.blue} testid="autonomy-applied"
          sub={shown.improvements?.held_for_human ? `${shown.improvements.held_for_human} held for you` : null} />
        {(shown.claims?.legacy_records ?? 0) > 0 && (
          <Tile label="legacy records reported" value={shown.claims.legacy_records} color={C.slate} testid="autonomy-legacy"
            sub="pre-register, not decided" />
        )}
      </div>

      {(shown.claims?.legacy_records ?? 0) > 0 && cfg.legacy_claims !== 'close' && (
        <div className="mt-3 p-2.5 rounded-sm text-[11px] flex items-start justify-between gap-3 flex-wrap"
          style={{ background: 'rgba(232,160,32,0.08)' }} data-testid="autonomy-legacy-note">
          <p style={{ color: '#8A5A10' }}>
            <b>{shown.claims.legacy_records} open claims pre-date the promise register</b> — their reserves and dates of loss
            do not reconcile to the policy term, so the adjudicator reports them instead of writing a decline letter on
            data it cannot stand behind. Closing them as legacy records with no payment is one instruction.
          </p>
          {isAdmin && (
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => saveConfig({ legacy_claims: 'close' })}
              className="h-7 text-[11px] rounded-sm" data-testid="autonomy-close-legacy-btn">Close them as legacy</Button>
          )}
        </div>
      )}

      {/* Parameters */}
      <div className="mt-4 pt-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
        <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: C.slate }}>The parameters it runs on</p>
        <div className="flex gap-3 flex-wrap items-end" data-testid="autonomy-config">
          {EDITABLE.map(f => (
            <div key={f.key} className="min-w-[8.5rem]">
              <p className="text-[10px]" style={{ color: C.slate }}>{f.label}</p>
              {isAdmin ? (
                <Input
                  className="h-7 text-xs rounded-sm w-32"
                  data-testid={`autonomy-cfg-${f.key}`}
                  placeholder={f.hint}
                  value={edits[f.key] ?? (cfg[f.key] ?? '')}
                  onChange={e => setEdits(v => ({ ...v, [f.key]: e.target.value }))}
                  onBlur={e => {
                    const raw = e.target.value.trim();
                    const current = cfg[f.key];
                    if (raw === String(current ?? '')) return;
                    saveConfig({ [f.key]: raw === '' ? null : Number(raw) });
                  }} />
              ) : (
                <p className="text-xs font-semibold" style={{ color: C.navy }}>{cfg[f.key] ?? 'none'}</p>
              )}
              <p className="text-[9px] mt-0.5" style={{ color: C.slate }}>{f.hint}</p>
            </div>
          ))}
        </div>
        <p className="text-[11px] mt-3 flex items-start gap-1.5" style={{ color: C.violet }} data-testid="autonomy-wording-carveout">
          <Lock className="w-3 h-3 mt-0.5 shrink-0" />
          Policy wording is the one change the loop may propose but never publish — the version pins are immutable for
          the life of a policy and publishing wording is a licensed act. There is no setting that changes this.
        </p>
      </div>

      {/* Carve-outs */}
      <div className="mt-4 pt-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.15)' }} data-testid="autonomy-carve-outs">
        <p className="text-[10px] uppercase tracking-wider mb-1.5 flex items-center gap-1.5" style={{ color: C.slate }}>
          <ShieldCheck className="w-3 h-3" /> Not the machine's call ({carve.total ?? 0})
        </p>
        {carve.items?.length ? (
          <div className="space-y-1">
            {carve.items.map(i => (
              <a key={i.kind} href={i.where} className="flex items-start gap-2 text-[11px] hover:underline" data-testid={`carve-${i.kind}`}>
                <span className="px-1.5 rounded text-[10px] font-bold shrink-0" style={{ background: 'rgba(108,75,181,0.12)', color: '#5B3E9E' }}>{i.count}</span>
                <span style={{ color: C.navy }}>{i.detail}</span>
              </a>
            ))}
          </div>
        ) : <p className="text-[11px]" style={{ color: C.green }} data-testid="carve-empty">Nothing is waiting on you.</p>}
        <p className="text-[10px] mt-2 italic" style={{ color: C.slate }}>{carve.note}</p>
      </div>

      {/* Disposal register */}
      {(data.disposals || []).length > 0 && (
        <div className="mt-4 pt-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
          <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: C.slate }}>
            Disposal register — every autonomous decision, with the reasoning it was made on
          </p>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-[11px]" data-testid="autonomy-disposals">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
                  <th className="py-1 pr-2">When</th><th className="py-1 pr-2">What</th>
                  <th className="py-1 pr-2">Outcome</th><th className="py-1 pr-2">Subject</th>
                  <th className="py-1">On what basis</th>
                </tr>
              </thead>
              <tbody>
                {data.disposals.map(d => (
                  <tr key={d.disposal_id} className="border-t align-top" style={{ borderColor: 'rgba(136,150,168,0.12)' }}
                    data-testid={`disposal-${d.disposal_id}`}>
                    <td className="py-1.5 pr-2 whitespace-nowrap text-[10px]" style={{ color: C.slate }}>{String(d.at).slice(5, 16).replace('T', ' ')}</td>
                    <td className="py-1.5 pr-2" style={{ color: C.slate }}>{d.kind}</td>
                    <td className="py-1.5 pr-2 font-semibold whitespace-nowrap" style={{ color: OUTCOME_COLOR[d.outcome] || C.navy }}>
                      {String(d.outcome).replace(/_/g, ' ')}
                    </td>
                    <td className="py-1.5 pr-2" style={{ color: C.navy }}>
                      {d.business_name || d.policy_number || d.claim_reference || d.renewal_reference || d.class_id || '—'}
                    </td>
                    <td className="py-1.5" style={{ color: C.slate }}>{d.rationale}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AutonomyCard;
